import { useEffect, useState } from 'react';
import styles from "../RightSidebar/rightsidebar.module.css";
import { appendExpandedTimeLabel, getInitials, getConsistentColor, removedSeconds, scrollToBookingCard } from '../../Utils/utils';
import DropDownIcon from "../Icons/DropDownIcon";
import Guesticon from "../../Assets/images/guestIcon.svg";
import BookingDetails from '../BookingDetails/BookingDetails';
import PrimiumMemberIcon from '../Icons/PrimiumMemberIcon';
import { RequestedTherapist } from '../Badges/RequestedTherapist';
import BookingPaymentDetails from '../BookingPaymentDetails/BookingPaymentDetails';
import DropdownImageTrigger from "../DropDown/DropdownImageTrigger";
import menuIcon from "../../Assets/images/menu.svg";
import { SaleNote } from "../SaleNote/SaleNote";
import { SaleCancelReason } from "../SaleCancelReason/SaleCancelReason";
import { useGlobalContext } from "../../Context/GlobalContext";
import MemberRateBadge from "../Badges/MemberRateBadge";
import SaleInfoLoader from './SaleInfoLoader/SaleInfoLoader';
import { toast } from 'react-toastify';
import { emailBookingInvoice } from '../../APICalling/APICalling';
import { useInvoiceEmailCooldown } from '../../Utils/useInvoiceEmailCooldown';
import { showAppToast } from '../../Utils/appToast';
import { downloadFileWithAuth } from '../../Utils/downloadFileWithAuth';
import logger from '../../Utils/logger';
import { isPrimaryCustomerGroup } from '../../Utils/liveGuestCheckinTag';

const SaleInfoPage = ({
     showAddNotePopup,
     saleNote,
     showVoidSalePopup,
     isSalesFlow = false,
     onOpenRelatedSale,
}) => {
    const [collapsedCommissions, setCollapsedCommissions] = useState(new Set());
    const isCommissionExpanded = (key) => !collapsedCommissions.has(key);

    const {
        sidebarLoading,
        bookingsList,
        bookingDetails,
		customerBookingItems,
		selectedOutletData,
		selectedDate,
     } = useGlobalContext();

    const bookingId = bookingDetails?.id;
    const saleId = bookingDetails?.sale_id ?? bookingId;
    const invoiceBaseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
    const [customerEmail, setCustomerEmail] = useState(bookingDetails?.user?.email || '');
    const hasCustomerEmail = Boolean(String(customerEmail || '').trim());
    const {
        startCooldown: startInvoiceEmailCooldown,
        getRemaining: getInvoiceEmailCooldownRemaining,
    } = useInvoiceEmailCooldown();
    const invoiceEmailCooldownRemaining = getInvoiceEmailCooldownRemaining(bookingId);
    const isInvoiceEmailCoolingDown = invoiceEmailCooldownRemaining > 0;
    const emailInvoiceTooltip = isInvoiceEmailCoolingDown
        ? 'Invoice emailed. Please wait before sending again.'
        : (!hasCustomerEmail ? 'Customer has no email address.' : '');

    useEffect(() => {
        setCustomerEmail(bookingDetails?.user?.email || '');
    }, [bookingDetails?.user?.email]);

    useEffect(() => {
        const handleCustomerUpdated = (event) => {
            const updatedCustomer = event?.detail?.customer;
            const currentCustomerId = bookingDetails?.user?.id;
            if (!updatedCustomer || !currentCustomerId) {
                return;
            }
            if (String(updatedCustomer.id) !== String(currentCustomerId)) {
                return;
            }
            setCustomerEmail(updatedCustomer.email || '');
        };

        window.addEventListener('customerUpdated', handleCustomerUpdated);
        return () => window.removeEventListener('customerUpdated', handleCustomerUpdated);
    }, [bookingDetails?.user?.id]);

    const openInvoiceDocument = async (format) => {
        if (!saleId) {
            toast.error('Sale ID not found.');
            return;
        }

        if (!invoiceBaseUrl) {
            toast.error('Invoice base URL is not configured.');
            return;
        }

        try {
            await downloadFileWithAuth(
                `${invoiceBaseUrl}/sales/${saleId}/invoice/${format}`,
                `invoice.${format}`
            );
        } catch (error) {
            logger.error('Invoice download failed:', error);
            toast.error(error?.message || 'Failed to download invoice');
        }
    };

    const handleEmailInvoice = async () => {
        if (!bookingId) {
            showAppToast('error', 'Sale ID not found.');
            return;
        }

        try {
            const response = await emailBookingInvoice(bookingId);
            const payload = response?.response?.data ?? response?.data ?? response ?? {};
            const nestedPayload = payload?.data && typeof payload.data === 'object' ? payload.data : {};
            const isExplicitFailure = [payload?.success, nestedPayload?.success,payload?.status, nestedPayload?.status,].some((value) => value === false || value === 'false' || value === 0 || value === '0');
            const isSuccess =
                payload?.success === true ||
                nestedPayload?.success === true ||
                payload?.status === true ||nestedPayload?.status === true ||
                (
                    payload?.success !== false &&
                    nestedPayload?.success !== false &&
                    payload?.status !== false && nestedPayload?.status !== false &&
                    !payload?.errors &&
                    !nestedPayload?.errors
                );
            const successMessage = payload?.message || nestedPayload?.message || 'Invoice emailed successfully.';
            const errorMessage = payload?.message || nestedPayload?.message || 'Failed to email invoice.';
            const isMissingEmail = errorMessage === 'Customer has no email address.';

            if (!isExplicitFailure && isSuccess) {
                showAppToast('success', successMessage);
                startInvoiceEmailCooldown(bookingId);
                return;
            }

            if (isMissingEmail) {
                showAppToast('info', errorMessage);
                return;
            }

            showAppToast('error', errorMessage);
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to email invoice.';

            if (errorMessage === 'Customer has no email address.') {
                showAppToast('info', errorMessage);
                return;
            }

            showAppToast('error', errorMessage);
        }
    };

    const toggleCommission = (bookingKey) => {
        setCollapsedCommissions((prev) => {
            const next = new Set(prev);
            if (next.has(bookingKey)) next.delete(bookingKey);
            else next.add(bookingKey);
            return next;
        });
    };

    const currentBooking = (() => {
        if (!bookingsList || !Array.isArray(bookingsList) || bookingsList.length === 0) {
            return null;
        }
        if (!bookingDetails) {
            return null;
        }

        // Try matching by booking_id first, then by id, with type coercion
        const bookingId = bookingDetails?.id;
        if (!bookingId) return null;

        return bookingsList.find((b) => {
            // Convert both to strings for comparison to handle type mismatches
            const bBookingId = String(b.booking_id || b.id || '');
            const apiBookingId = String(bookingId);

            return bBookingId === apiBookingId && bBookingId !== '';
        }) || null;
    })();

    const status = bookingDetails?.status?.toLowerCase() || currentBooking?.status?.toLowerCase() || '';
    const statusLabel =
        (['completed', 'cancelled', 'closed', 'paid'].includes(status) && 'Closed') ||
        (status === 'voided' && 'Voided') ||
        (status === 'refunded' && 'Refunded') ||
        '';
    const shouldHideServiceDetails = status === 'voided' || status === 'refunded';
    const saleTotalAmount = (
        parseFloat(bookingDetails?.sub_total || 0) +
        parseFloat(bookingDetails?.gst_amount || 0) +
        parseFloat(bookingDetails?.cash_rounding || 0)
    ).toFixed(2);
    const refundSummaryAmount = parseFloat(bookingDetails?.grand_total ?? saleTotalAmount ?? 0).toFixed(2);

    const canShowInvoiceOptions = status === 'completed' || status === 'closed';
    const hasChildRefundSale = Boolean(bookingDetails?.child_sale_id);
    const refundSaleOption = {
        label: "Refund Sale",
        action: showVoidSalePopup,
        disabled: hasChildRefundSale,
        tooltip: hasChildRefundSale ? "Already refunded" : "",
    };

    const invoiceOptions = [
        { label: "Download PDF Invoice", action: () => openInvoiceDocument('pdf') },
        // { label: "Download Doc Invoice", action: () => openInvoiceDocument('doc') },
        // {
        //     label: isInvoiceEmailCoolingDown ? `Email Invoice (${invoiceEmailCooldownRemaining}s)` : "Email Invoice",
        //     action: handleEmailInvoice,
        //     disabled: !hasCustomerEmail || isInvoiceEmailCoolingDown,
        //     tooltip: emailInvoiceTooltip,
        // },
    ];

    const menuOptionsMap = {
        'completed': [
            { label: saleNote ? "Edit Notes" : "Add Notes", action: showAddNotePopup },
            refundSaleOption,
            ...(canShowInvoiceOptions ? invoiceOptions : []),
        ],
        'closed': [
            { label: saleNote ? "Edit Notes" : "Add Notes", action: showAddNotePopup },
            refundSaleOption,
            ...(canShowInvoiceOptions ? invoiceOptions : []),
        ],
        'cancelled': [
            { label: saleNote ? "Edit Notes" : "Add Notes", action: showAddNotePopup },
            refundSaleOption,
        ],
        'voided': [
            { label: saleNote ? "Edit Notes" : "Add Notes", action: showAddNotePopup },
        ]
    };
    const menuOptions = menuOptionsMap[status] || [];

    // const safeParseAdditional = (val) => {
    // 	if (!val) return {};
    // 	if (typeof val === 'object') return val || {};
    // 	try { return JSON.parse(val); } catch { return {}; }
    // };

    // Calculate Commission: parent commission + sum of all split service commissions
    // const calculateTotalCommission = (booking) => {
    // 	if (!booking) return 0;

    // 	const additionalInfo = safeParseAdditional(booking.additional);
    // 	const parentCommission = Number(additionalInfo?.commission?.rate) || 0;

    // 	const splitServiceCommissions = booking?.SplitedService?.reduce((sum, splitedService) => {
    // 		const splitedServiceAdditionalInfo = safeParseAdditional(splitedService.additional);
    // 		return sum + (Number(splitedServiceAdditionalInfo?.commission?.rate) || 0);
    // 	}, 0) || 0;

    // 	return parentCommission + splitServiceCommissions;
    // };

    const getServiceTimeDisplay = (booking) => {
		let displayTime = "";
		if (booking?.service_time) {
			displayTime = removedSeconds(booking.service_time);
		} else if (booking?.start_time) {
			const [h, m] = String(booking.start_time).split(':');
			const hour = parseInt(h, 10);
			const h12 = hour % 12 || 12;
			const ampm = hour >= 12 ? 'PM' : 'AM';
			displayTime = `${String(h12).padStart(2, '0')}:${(m || '00').slice(0, 2)} ${ampm}`;
		} else if (booking?.service_at) {
			const part = booking.service_at.split(' ')[1];
			if (part) {
				const [hh, mm] = part.split(':');
				const hour = parseInt(hh, 10);
				const h12 = hour % 12 || 12;
				const ampm = hour >= 12 ? 'PM' : 'AM';
				displayTime = `${String(h12).padStart(2, '0')}:${(mm || '00').slice(0, 2)} ${ampm}`;
			}
		}
		const labelContextDate = !isSalesFlow
			? (selectedDate || booking?.service_date || bookingDetails?.service_date)
			: (booking?.service_date || bookingDetails?.service_date);
		return appendExpandedTimeLabel(
			displayTime,
			booking,
			selectedOutletData?.timing,
			labelContextDate
		);
	};

    const getTherapistNameWithClawback = (booking) => {
        const therapistName = booking?.therapist ?? booking?.therapist_name ?? 'N/A';

        return (
            <>
                {therapistName}
                {booking?.commission_clawbacked === true && (
                    <em> (Commission clawbacked)</em>
                )}
            </>
        );
    };


    // Transform booking_item to group split services under parent services
    const transformCustomerData = (customerData) => {
        if (!customerData) return {};

        const transformedData = {};

        Object.entries(customerData).forEach(([customerName, bookings]) => {
            const transformedBookings = [];
            const processedBookingIds = new Set();

            bookings.forEach(booking => {
                const bookingId = booking.id ?? booking.booking_item_id;
                if (processedBookingIds.has(bookingId)) return;

                const splited = booking.splited === true || booking.splited === "true";

                if (splited && booking.parent_booking_item_id === null) {
                    const childServices = bookings.filter(childBooking =>
                        childBooking.parent_booking_item_id === bookingId &&
                        (childBooking.splited === true || childBooking.splited === "true")
                    );
                    childServices.forEach(child => processedBookingIds.add(child.id ?? child.booking_item_id));
                    transformedBookings.push({ ...booking, SplitedService: childServices });
                } else if (booking.splited === false || booking.splited === "false" || booking.parent_booking_item_id !== null) {
                    const isChildOfSplitService = bookings.some(parentBooking =>
                        (parentBooking.id ?? parentBooking.booking_item_id) === booking.parent_booking_item_id &&
                        (parentBooking.splited === true || parentBooking.splited === "true")
                    );
                    if (!isChildOfSplitService) transformedBookings.push(booking);
                }
            });

            transformedData[customerName] = transformedBookings;
        });

        return transformedData;
    };

    const UpdatedCustomerBookingItems = transformCustomerData(customerBookingItems);

    return (
        <>
            {sidebarLoading ? (
                <SaleInfoLoader />
            ) : (
                <div className={`${styles.saleInfo} h-100`}>
                    {!isSalesFlow && (
                        <div className={styles.rightHeader}>
                            <div className={styles.dFlex}>
                                <h5>Sale <i>#{bookingDetails?.sale_id ?? bookingDetails?.id}{statusLabel ? ` (${statusLabel})` : ''}</i></h5>
                            </div>
                            <div className={`${styles.menuIcon}`}>
                                <DropdownImageTrigger
                                    iconSrc={menuIcon}
                                    options={menuOptions}
                                    menuClass={`${styles.customMenuClass} ${styles["status" + status]}`}
                                />
                            </div>
                        </div>
                    )}
                    <div className={`${styles.infoWrapper} ${styles.paymentContent}`}>

                    {bookingDetails?.parent_sale_id && (
                        <div className={styles.refundSaleInfoHeader}>
                            This refund is for {" "}
                            <button 
                            type="button"
                            className={styles.refundSaleInfoHeaderButton}
                            onClick={() => onOpenRelatedSale?.(bookingDetails.parent_sale_id)}
                            >
                            Sale #{bookingDetails?.parent_sale_id}
                            </button>
                        </div>
                    )}

                        {/* Primary Customer Information */}
                        <div className={styles.customerInfoWrapper}>
                            <div className={styles.customerCard}>
                                <div className={`${styles.avatarWrapper} cursor-pointer`}
                                    onClick={() => {
                                        const contactNumber = bookingDetails?.user?.contact_number;

                                        const userId = bookingDetails?.user?.id;
                                        if (contactNumber || userId) {
                                            const customerData = {
                                                id: userId,
                                                name: bookingDetails?.user?.name,
                                                contact_number: contactNumber,
                                                email: bookingDetails?.user?.email,
                                            };

                                            const event = new CustomEvent('openCustomerDetails', {
                                                detail: { customer: customerData }
                                            });
                                            window.dispatchEvent(event);
                                        }
                                    }}
                                >
                                    <div className={styles.avatar} style={{ backgroundColor: getConsistentColor(bookingDetails?.user?.id, bookingDetails?.user?.name) }}>
                                        <span>{getInitials(bookingDetails?.user?.name)}</span>

                                        {(Number(bookingDetails?.membership) === 1 || Number(bookingDetails?.user?.membership) === 1) && (
                                            <span className={styles.membershipIcon}>
                                                <PrimiumMemberIcon />
                                            </span>
                                        )}

                                    </div>
                                </div>
                                <div
                                    className={styles.customerDetails}>
                                    <div className="cursor-pointer"
                                        onClick={() => {
                                            const contactNumber = bookingDetails?.user?.contact_number;

                                            const userId = bookingDetails?.user?.id;
                                            if (contactNumber || userId) {
                                                const customerData = {
                                                    id: userId,
                                                    name: bookingDetails?.user?.name,
                                                    contact_number: contactNumber,
                                                    email: bookingDetails?.user?.email,
                                                };

                                                const event = new CustomEvent('openCustomerDetails', {
                                                    detail: { customer: customerData }
                                                });
                                                window.dispatchEvent(event);
                                            }
                                        }}
                                    >
                                        <p className={styles.customerMobile}>
                                            {bookingDetails?.user?.contact_number}
                                        </p>
                                        <p className={styles.customerName}>
                                            {bookingDetails?.user?.name}
                                        </p>
                                    </div>


                                    {bookingDetails?.user?.note?.trim() && (
                                        <div className={styles.customerNotes}>
                                            <p className={styles.customerNotesText}>
                                                {bookingDetails?.user?.note}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {(status === 'cancelled' || status === 'voided' || status === 'refunded') && bookingDetails?.cancel_reason && (
                            <SaleCancelReason reason={bookingDetails?.cancel_reason} remark={bookingDetails?.cancel_remark} />
                        )}

                        {saleNote?.trim() && (
                            <SaleNote saleNote={saleNote} showAddNotePopup={showAddNotePopup} />
                        )}

                        {!shouldHideServiceDetails && (
                        <div className={styles.servicesInfoWrapper}>
                            {(() => {
                                // Separate primary customer and guest data
                                const primaryCustomerData = [];
                                const guestData = [];

                                if (UpdatedCustomerBookingItems) {
                                    Object.entries(UpdatedCustomerBookingItems).forEach(([customerName, bookings]) => {
                                        if (isPrimaryCustomerGroup(customerName, bookings, bookingDetails?.user?.name)) {
                                            primaryCustomerData.push({ customerName, bookings });
                                        } else {
                                            guestData.push({ customerName, bookings });
                                        }
                                    });
                                }

                                return (
                                    <>
                                        {/* Primary Customer Data - Show first without guest icon and name */}
                                        {primaryCustomerData.map(({ customerName, bookings }) => (
                                            <div key={`primary-${customerName}`} className={styles.paxBlock}>
                                                {bookings.map((booking, index) => {
                                                    const rawCommissionRate = booking?.commission?.total_rate ?? booking?.commission?.rate;
                                                    const finalCommissionRate = Number(rawCommissionRate);
                                                    const safeCommissionRate = Number.isFinite(finalCommissionRate) ? finalCommissionRate : 0;
                                                    return (
                                                        <div key={index} className={styles.serviceLine}>
                                                            <div className={styles.serviceDetails}
                                                                onClick={() => { if (booking?.id) { scrollToBookingCard(booking?.id); } }}
                                                            >
                                                                {booking?.voucher?.code ? (
                                                                    <div
                                                                        className={`${styles.spacing} d-flex justify-between cursor-pointer`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleCommission(booking?.id ?? booking?.booking_item_id ?? `primary-${index}`);
                                                                        }}
                                                                    >
                                                                        <div className={`${styles.toggleService} d-flex gap-5`}>
                                                                            <DropDownIcon
                                                                                size={16}
                                                                                color="var(--neutral-900)"
                                                                                className={`${styles.dropdownIcon} ${isCommissionExpanded(booking?.id ?? booking?.booking_item_id ?? `primary-${index}`) ? styles.rotated : ''}`}
                                                                            />
                                                                            <div className={`${styles.selectedServiceHead} mb-0`}>
                                                                                <h3>{`${booking.duration} Mins ${booking.service}`}</h3>
                                                                                <div className={styles.voucherCard}>
                                                                                    Service voucher applied ({booking?.voucher?.code})
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <p className={`${styles.price} d-flex flex-end`}>
                                                                            <strike>${parseFloat(booking?.voucher?.price).toFixed(2)}</strike>
                                                                            <span>${(parseFloat(booking?.price || 0) + parseFloat(booking?.addons_total || 0)).toFixed(2)}</span>
                                                                        </p>
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className={`${styles.spacing} d-flex justify-between cursor-pointer`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleCommission(booking?.id ?? booking?.booking_item_id ?? `primary-${index}`);
                                                                        }}
                                                                    >
                                                                        <div className={`${styles.dFlex} ${styles.toggleService} gap-5`}>
                                                                            <DropDownIcon
                                                                                size={16}
                                                                                color="var(--neutral-900)"
                                                                                className={`${styles.dropdownIcon} ${isCommissionExpanded(booking?.id ?? booking?.booking_item_id ?? `primary-${index}`) ? styles.rotated : ''}`}
                                                                            />
                                                                            <div className={`${styles.selectedServiceHead} mb-0`}>
                                                                                <h3>{`${booking.duration} Mins ${booking.service}`}</h3>
                                                                            </div>
                                                                        </div>
                                                                        <p className={styles.price}>
                                                                            <span>${(parseFloat(booking?.price || 0) + parseFloat(booking?.addons_total || 0)).toFixed(2)}</span>
                                                                        </p>
                                                                    </div>
                                                                )}


                                                                {booking?.addons?.length > 0 && (
                                                                    <div className={`${styles.dFlex} ${styles.spacing}`}>
                                                                        <label className={styles.label}>Add-on:</label>
                                                                        <p className={styles.sameFont}>
                                                                            {booking.addons.map((addon) => addon.name).join(', ')}
                                                                        </p>
                                                                    </div>
                                                                )}


                                                                <div className={styles.memberRateBadgeContainer}>

                                                                {booking?.price_adjustment ? (
                                                                    <div className={styles.spacing}><MemberRateBadge text={booking?.price_adjustment?.title} /></div>
                                                                ):(
                                                                    <>
                                                                    {(booking?.isMemberRateApplied === 1 || booking?.isMemberRateApplied === true) && (
                                                                        <div className={styles.spacing}><MemberRateBadge text="Member Rate (OP)" /></div>
                                                                    )}

                                                                    {(booking?.isMemberRateApplied === 0 || booking?.isMemberRateApplied === false) &&
                                                                    (booking?.isMembershipApplied === 1 || booking?.isMembershipApplied === true) && (
                                                                        <div className={styles.spacing}><MemberRateBadge text="Member Rate" /></div>
                                                                    )}
                                                                    </>
                                                                )}

                                                                    {/* {(booking?.isMemberRateApplied === 1 || booking?.isMemberRateApplied === true) && (
                                                                        <div className={styles.spacing}><MemberRateBadge text="Member Rate (OP)" /></div>
                                                                    )}

                                                                    {(booking?.isMemberRateApplied === 0 || booking?.isMemberRateApplied === false) &&
                                                                    (booking?.isMembershipApplied === 1 || booking?.isMembershipApplied === true) && (
                                                                        <div className={styles.spacing}><MemberRateBadge text="Member Rate" /></div>
                                                                    )}

                                                                    {booking?.price_adjustment && (
                                                                        <div className={styles.spacing}><MemberRateBadge text={booking?.price_adjustment?.title} /></div>
                                                                    )} */}
                                                                </div>


                                                                <div className={`${styles.selectedTherapistCard} ${styles.spacing} d-flex justify-between`}>
                                                                    <div className={`${styles.dFlex}`}>
                                                                        <label className={styles.label}>With:</label>
                                                                        <span
                                                                            className={`${styles.therapistCircle} ${(booking?.gender ?? booking?.therapist_gender) === "female" ? styles.female :
                                                                                (booking?.gender ?? booking?.therapist_gender) === "male" ? styles.male : ''
                                                                                }`}
                                                                            title={booking?.therapist_pager_number ?? 'N/A'}
                                                                        >
                                                                            {booking?.therapist_pager_number ?? 'N/A'}
                                                                        </span>
                                                                        <p className={styles.sameFont} title={(booking?.therapist ?? booking?.therapist_name) ?? 'N/A'}>{getTherapistNameWithClawback(booking)}</p>
                                                                    </div>
                                                                    {booking?.requested_person === 1 && <RequestedTherapist />}
                                                                </div>

                                                                {booking?.commission?.rate && isCommissionExpanded(booking?.id ?? booking?.booking_item_id ?? `primary-${index}`) &&
                                                                    <div className={`${styles.commissionCard} ${styles.dFlex} ${styles.spacing}`}>
                                                                        <div className={styles.dFlex}>
                                                                            <label className={styles.label}>Commission:</label>
                                                                            <p className={styles.sameFont}>${safeCommissionRate.toFixed(2)}</p>
                                                                        </div>
                                                                        {booking?.commission_price_adjustment && <MemberRateBadge
                                                                            text={booking?.commission_price_adjustment?.title} color="var(--primary-100)" />}
                                                                    </div>
                                                                }

                                                                {/* SPLITED SERVICE  START */}
                                                                {booking?.SplitedService?.length > 0 && (
                                                                    <div className={styles.splitedService}>
                                                                        {booking?.SplitedService?.map((splitedService) => {
                                                                            return (
                                                                                <div key={splitedService?.id ?? splitedService?.booking_item_id}>
                                                                                    <div className={`${styles.selectedTherapistCard} d-flex justify-between`}>
                                                                                        <div className={`${styles.dFlex}`}>
                                                                                            <label className={styles.label}>With:</label>
                                                                                            <span
                                                                                                className={`${styles.therapistCircle} ${(splitedService?.gender ?? splitedService?.therapist_gender) === "female" ? styles.female :
                                                                                                    (splitedService?.gender ?? splitedService?.therapist_gender) === "male" ? styles.male : ''
                                                                                                    }`}
                                                                                            >
                                                                                                {splitedService?.therapist_pager_number ?? 'N/A'}
                                                                                            </span>
                                                                                            <p className={styles.sameFont}>{getTherapistNameWithClawback(splitedService)}</p>
                                                                                        </div>
                                                                                        {splitedService?.requested_person === 1 && <RequestedTherapist />}
                                                                                    </div>

                                                                                    <div className={`${styles.dFlex} ${styles.spacing}`}>
                                                                                        <div className={`${styles.dFlex}`}>
                                                                                            <label className={styles.label}>For:</label>
                                                                                            <p className={styles.sameFont}>{splitedService?.duration ?? splitedService?.duration ?? ''} mins</p>
                                                                                        </div>
                                                                                        <div className={`${styles.dFlex} ${styles.space}`}>
                                                                                            <label className={styles.label}>At:</label>
                                                                                            <p className={styles.sameFont}>{getServiceTimeDisplay(splitedService)}</p>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                                {/* SPLITED SERVICE  END */}

                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}

                                        {/* Guest Data - Show after primary customer data with guest icon and name */}
                                        {guestData.map(({ customerName, bookings }) => (
                                            <div key={`guest-${customerName}`} className={styles.paxBlock}>
                                                <div className={`${styles.dFlex} ${styles.paxHeader}`}>
                                                    <img src={Guesticon} alt="guestIcon" />
                                                    <p className={styles.paxName}>{customerName}</p>
                                                </div>
                                                {bookings.map((booking, index) => {
                                                    const rawCommissionRate = booking?.commission?.total_rate ?? booking?.commission?.rate;
                                                    const finalCommissionRate = Number(rawCommissionRate);
                                                    const safeCommissionRate = Number.isFinite(finalCommissionRate) ? finalCommissionRate : 0;
                                                    return (
                                                        <div key={index} className={styles.serviceLine}>
                                                            <div className={styles.serviceDetails}
                                                                onClick={() => { if (booking?.booking_item_id) { scrollToBookingCard(booking?.booking_item_id); } }}
                                                            >
                                                                {booking?.voucher?.code ? (
                                                                    <div
                                                                        className={`${styles.spacing} d-flex justify-between cursor-pointer`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleCommission(booking?.id ?? booking?.booking_item_id ?? `guest-${customerName}-${index}`);
                                                                        }}
                                                                    >
                                                                        <div className={`${styles.toggleService} d-flex gap-5`}>
                                                                            <DropDownIcon
                                                                                size={16}
                                                                                color="var(--neutral-900)"
                                                                                className={`${styles.dropdownIcon} ${isCommissionExpanded(booking?.id ?? booking?.booking_item_id ?? `guest-${customerName}-${index}`) ? styles.rotated : ''}`}
                                                                            />
                                                                            <div className={`${styles.selectedServiceHead} mb-0`}>
                                                                                <h3>{`${booking?.duration} Mins ${booking.service}`}</h3>
                                                                                <div className={styles.voucherCard}>
                                                                                    Service voucher applied ({booking?.voucher?.code})
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <p className={`${styles.price} d-flex`}>
                                                                            <strike>${parseFloat(booking?.voucher?.price).toFixed(2)}</strike>
                                                                            <span>${(parseFloat(booking?.price || 0) + parseFloat(booking?.addons_total || 0)).toFixed(2)}</span>
                                                                        </p>
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className={`${styles.spacing} d-flex justify-between cursor-pointer`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleCommission(booking?.id ?? booking?.booking_item_id ?? `guest-${customerName}-${index}`);
                                                                        }}
                                                                    >
                                                                        <div className={`${styles.dFlex} ${styles.toggleService} gap-5`}>
                                                                            <DropDownIcon
                                                                                size={16}
                                                                                color="var(--neutral-900)"
                                                                                className={`${styles.dropdownIcon} ${isCommissionExpanded(booking?.id ?? booking?.booking_item_id ?? `guest-${customerName}-${index}`) ? styles.rotated : ''}`}
                                                                            />
                                                                            <div className={`${styles.selectedServiceHead} mb-0`}>
                                                                                <h3>{`${booking?.duration} Mins ${booking.service}`}</h3>
                                                                            </div>
                                                                        </div>
                                                                        <p className={styles.price}>
                                                                            <span>${(parseFloat(booking?.price || 0) + parseFloat(booking?.addons_total || 0)).toFixed(2)}</span>
                                                                        </p>
                                                                    </div>
                                                                )}

                                                                {booking?.addons?.length > 0 && (
                                                                    <div className={`${styles.dFlex} ${styles.spacing}`}>
                                                                        <label className={styles.label}>Add-on:</label>
                                                                        <p className={styles.sameFont}>
                                                                            {booking.addons.map((addon) => addon.name).join(', ')}
                                                                        </p>
                                                                    </div>
                                                                )}


                                                                <div className={styles.memberRateBadgeContainer}>
                                                                {booking?.price_adjustment ? (
                                                                    <div className={styles.spacing}><MemberRateBadge text={booking?.price_adjustment?.title} /></div>
                                                                ):(
                                                                    <>
                                                                    {(booking?.isMemberRateApplied === 1 || booking?.isMemberRateApplied === true) && (
                                                                        <div className={styles.spacing}><MemberRateBadge text="Member Rate (OP)" /></div>
                                                                    )}

                                                                    {(booking?.isMemberRateApplied === 0 || booking?.isMemberRateApplied === false) &&
                                                                        (booking?.isMembershipApplied === 1 || booking?.isMembershipApplied === true) && (
                                                                            <div className={styles.spacing}><MemberRateBadge text="Member Rate" /></div>
                                                                        )}

                                                                    </>
                                                                )}
                                                                </div>

                                                                <div className={`${styles.selectedTherapistCard} ${styles.spacing} d-flex justify-between`}>
                                                                    <div className={`${styles.dFlex}`}>
                                                                        <label className={styles.label}>With:</label>
                                                                        <span
                                                                            className={`${styles.therapistCircle} ${(booking?.gender ?? booking?.therapist_gender) === "female" ? styles.female :
                                                                                (booking?.gender ?? booking?.therapist_gender) === "male" ? styles.male : ''
                                                                                }`}
                                                                            title={booking?.therapist_pager_number ?? 'N/A'}
                                                                        >
                                                                            {booking?.therapist_pager_number ?? 'N/A'}
                                                                        </span>
                                                                        <p className={styles.sameFont} title={(booking?.therapist ?? booking?.therapist_name) ?? 'N/A'}>{getTherapistNameWithClawback(booking)}</p>
                                                                    </div>
                                                                    {booking?.requested_person === 1 && <RequestedTherapist />}
                                                                </div>

                                                                {booking?.commission?.rate && isCommissionExpanded(booking?.id ?? booking?.booking_item_id ?? `guest-${customerName}-${index}`) &&
                                                                    <div className={`${styles.commissionCard} ${styles.dFlex} ${styles.spacing}`}>
                                                                        <div className={styles.dFlex}>
                                                                            <label className={styles.label}>Commission:</label>
                                                                            <p className={styles.sameFont}>${safeCommissionRate.toFixed(2)}</p>
                                                                        </div>
                                                                        {booking?.commission_price_adjustment && <MemberRateBadge
                                                                            text={booking?.commission_price_adjustment?.title} color="var(--primary-100)" />}
                                                                    </div>
                                                                }

                                                                {/* SPLITED SERVICE  START */}
                                                                {booking?.SplitedService?.length > 0 && (
                                                                    <div className={styles.splitedService}>
                                                                        {booking?.SplitedService?.map((splitedService) => {
                                                                            return (
                                                                                <div key={splitedService?.id ?? splitedService?.booking_item_id}>
                                                                                    <div className={`${styles.selectedTherapistCard} d-flex justify-between`}>
                                                                                        <div className={`${styles.dFlex}`}>
                                                                                            <label className={styles.label}>With:</label>
                                                                                            <span
                                                                                                className={`${styles.therapistCircle} ${(splitedService?.gender ?? splitedService?.therapist_gender) === "female" ? styles.female :
                                                                                                    (splitedService?.gender ?? splitedService?.therapist_gender) === "male" ? styles.male : ''
                                                                                                    }`}
                                                                                            >
                                                                                                {splitedService?.therapist_pager_number ?? 'N/A'}
                                                                                            </span>
                                                                                            <p className={styles.sameFont}>{getTherapistNameWithClawback(splitedService)}</p>
                                                                                        </div>
                                                                                        {splitedService?.requested_person === 1 && <RequestedTherapist />}
                                                                                    </div>

                                                                                    <div className={`${styles.dFlex} ${styles.spacing}`}>
                                                                                        <div className={`${styles.dFlex}`}>
                                                                                            <label className={styles.label}>For:</label>
                                                                                            <p className={styles.sameFont}>{splitedService?.duration ?? splitedService?.duration ?? ''} mins</p>
                                                                                        </div>
                                                                                        <div className={`${styles.dFlex} ${styles.space}`}>
                                                                                            <label className={styles.label}>At:</label>
                                                                                            <p className={styles.sameFont}>{getServiceTimeDisplay(splitedService)}</p>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                                {/* SPLITED SERVICE  END */}

                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </>
                                );
                            })()}
                        </div>
                        )}

                        <div className={styles.priceInfoWrapper}>
                            <div className={styles.upperSection}>
                                {shouldHideServiceDetails ? (
                                    <>
                                        <div
                                            className={`${styles.payrow} ${styles.dFlex} ${styles.justifyBetween} ${styles.p20}`}>
                                            <p>Refund Amount</p>
                                            <p>${refundSummaryAmount}</p>
                                        </div>
                                        <div
                                            className={`${styles.payrow} ${styles.dFlex} ${styles.justifyBetween} ${styles.p20}`}>
                                            <p>Subtotal</p>
                                            <p>${refundSummaryAmount}</p>
                                        </div>
                                        <div
                                            className={`${styles.finalTotal} ${styles.payrow} ${styles.dFlex} ${styles.justifyBetween}`}>
                                            <p>Total Refund</p>
                                            <p>${refundSummaryAmount}</p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div
                                            className={`${styles.payrow} ${styles.dFlex} ${styles.justifyBetween} ${styles.p20}`}>
                                            <p>Subtotal</p>
                                            <p>${bookingDetails?.sub_total}</p>
                                        </div>
                                        <div
                                            className={`${styles.payrow} ${styles.dFlex} ${styles.justifyBetween} ${styles.p20}`}>
                                            <p>GST</p>
                                            <p>${bookingDetails?.gst_amount}</p>
                                        </div>
                                        {parseFloat(bookingDetails?.cash_rounding || 0) !== 0 && (
                                            <div
                                            className={`${styles.payrow} ${styles.dFlex} ${styles.justifyBetween} ${styles.p20}`}>
                                            <p>Cash Rounding</p>
                                            <p>
                                            {bookingDetails.cash_rounding < 0
                                            ? `- $${Math.abs(bookingDetails.cash_rounding).toFixed(2)}`
                                                : `$${Number(bookingDetails.cash_rounding).toFixed(2)}`}
                                                </p>   
                                        </div>
                                        )}
                                        <div
                                            className={`${styles.finalTotal} ${styles.payrow} ${styles.dFlex} ${styles.justifyBetween}`}>
                                            <p>Total</p>
                                            <p>${saleTotalAmount}</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className={styles.paymentDetail}>
                        <BookingPaymentDetails
                            refundMode={shouldHideServiceDetails}
                        />
                        </div>

                        <BookingDetails
                            heading={shouldHideServiceDetails ? "Refund Details" : "Sale Details"}
                            CreateDate={bookingDetails?.booking_created_at ?? bookingDetails?.created_at}
                            CreatedBy={bookingDetails?.created_by_name}
                            UpdatedDate={bookingDetails?.updated_at}
                            LastUpdatedBy={bookingDetails?.updated_by_name}
                            BookingDate={bookingDetails?.booking_date}
                            Source={bookingDetails?.source}
                        />

                    </div>
                </div>
            )}
        </>
    )
}

export default SaleInfoPage